// Buffers frontend errors and flushes them to the token-gated /dev-tools/client-logs endpoint.
// Port of urmate.app's devToolsLogger.js to TS + fetch. Same-origin (/dev-tools), Vite proxies it in dev.

// Hardcoded in the repo on purpose — shared dev token, NOT an env secret.
const DEVTOOLS_TOKEN = 'dt_callbot_8f2a1c7e9b3d4056';
const DEVTOOLS_BASE = '/dev-tools';
const FLUSH_INTERVAL_MS = 4000;
const MAX_BUFFER = 100;
const MAX_FIELD_LEN = 8000;

export type DevToolsLogEntry = {
  level: 'error' | 'warn' | 'info' | 'debug';
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  origin?: string;
  timestamp?: string;
};

let buffer: DevToolsLogEntry[] = [];
let active = false;
let timer: ReturnType<typeof setInterval> | null = null;

function clip(value: string | undefined, max = MAX_FIELD_LEN): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

export function getDevToolsToken(): string {
  return DEVTOOLS_TOKEN;
}

export function getDevToolsBase(): string {
  return DEVTOOLS_BASE;
}

export function recordDevToolsLog(entry: Partial<DevToolsLogEntry> & { message: string }): void {
  if (!active) return;
  if (buffer.length >= MAX_BUFFER) buffer.shift();
  buffer.push({
    level: entry.level ?? 'error',
    message: clip(entry.message) ?? '',
    stack: clip(entry.stack),
    url: clip(entry.url ?? (typeof location !== 'undefined' ? location.href : undefined), 500),
    userAgent: clip(typeof navigator !== 'undefined' ? navigator.userAgent : undefined, 500),
    origin: entry.origin,
    timestamp: entry.timestamp ?? new Date().toISOString(),
  });
}

export async function flushDevToolsLogs(): Promise<void> {
  if (!active || buffer.length === 0) return;
  const entries = buffer;
  buffer = [];
  try {
    const res = await fetch(`${DEVTOOLS_BASE}/client-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-DevTools-Token': DEVTOOLS_TOKEN },
      body: JSON.stringify({ entries }),
      keepalive: true,
    });
    if (res.status === 404) deactivateDevToolsLogger();
  } catch {
    // network error — drop this batch rather than requeue forever
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'hidden') void flushDevToolsLogs();
}

export function activateDevToolsLogger(): void {
  if (active) return;
  active = true;
  timer = setInterval(() => void flushDevToolsLogs(), FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

export function deactivateDevToolsLogger(): void {
  active = false;
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  document.removeEventListener('visibilitychange', onVisibilityChange);
  buffer = [];
}
